import React, { useMemo } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import {
  Badge,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  SvgIcon,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

const StatusCard = ({
  number,
  label,
  icon,
  button,
  color,
  noNumber,
  url,
  onClick,
  loading,
  // Inverts the palette (theme contrast background, secondary number chip)
  // for cards that must stand out from the rest
  contrast,
  // Adds a pulsing ring around the card
  pulse,
  ...props
}) => {
  const navigate = useNavigate()
  const colors = useMemo(() => {
    switch (color) {
      case 1:
        return {
          main: 'hsla(343,85%,63%, 1)',
          light: 'hsla(343, 85%, 73%, 1)',
          dark: 'hsl(343, 85%, 53%)',
        }
      case 2:
        return {
          main: 'hsla(242, 30%, 56%, 1)',
          light: 'hsla(242, 30%, 66%, 1)',
          dark: 'hsla(242, 30%, 46%, 1)',
        }
      case 3:
        return {
          main: 'hsla(196, 73%, 54%, 1)',
          light: 'hsla(196, 73%, 64%, 1)',
          dark: 'hsla(196, 73%, 44%, 1)',
        }
      case 4:
        return {
          main: 'hsla(181, 55%, 49%, 1)',
          light: 'hsla(181, 55%, 59%, 1)',
          dark: 'hsla(181, 55%, 39%, 1)',
        }
      case 5:
        return {
          main: 'hsla(206, 55%, 11%, 1)',
          light: 'hsla(206, 55%, 21%, 1)',
          dark: 'hsla(206, 55%, 1%, 1)',
        }
      case 6:
        return {
          main: 'hsla(339, 62%, 30%, 1)',
          light: 'hsla(339, 62%, 40%, 1)',
          dark: 'hsla(339, 62%, 20%, 1)',
        }
      case 7:
        return {
          main: 'hsla(90, 80%, 40%, 1)',
          light: 'hsla(90, 80%, 50%, 1)',
          dark: 'hsla(90, 80%, 30%, 1)',
        }
      case 8:
        return {
          main: 'hsla(223, 53%, 31%, 1)',
          light: 'hsla(223, 53%, 41%, 1)',
          dark: 'hsla(223, 53%, 21%, 1)',
        }
      case 9:
        return {
          main: 'hsla(175, 75%, 21%, 1)',
          light: 'hsla(175, 75%, 31%, 1)',
          dark: 'hsla(175, 75%, 11%, 1)',
        }
      case 10:
        return {
          main: 'hsla(343, 91%, 52%, 1)',
          light: 'hsla(343, 91%, 62%, 1)',
          dark: 'hsla(343, 91%, 42%, 1)',
        }
      case 11:
        return {
          main: 'hsla(356, 74%, 52%, 1)',
          light: 'hsla(356, 74%, 62%, 1)',
          dark: 'hsla(356, 74%, 42%, 1)',
        }
      case 12:
        return {
          main: 'hsla(201, 18%, 40%, 1)',
          light: 'hsla(201, 18%, 50%, 1)',
          dark: 'hsla(201, 18%, 30%, 1)',
        }
      case 13:
        return {
          main: 'hsla(42, 90%, 50%, 1)',
          light: 'hsla(42, 90%, 60%, 1)',
          dark: 'hsla(42, 90%, 40%, 1)',
        }
      case 14:
        return {
          main: 'hsla(24, 100%, 50%, 1)',
          light: 'hsla(24, 100%, 60%, 1)',
          dark: 'hsla(24, 100%, 40%, 1)',
        }

      case 15:
        return {
          main: 'hsla(200, 80%, 40%, 1)',
          light: 'hsla(200, 80%, 50%, 1)',
          dark: 'hsla(200, 80%, 30%, 1)',
        }
      case 16:
        return {
          main: 'hsla(270, 60%, 50%, 1)',
          light: 'hsla(270, 60%, 60%, 1)',
          dark: 'hsla(270, 60%, 40%, 1)',
        }
      case 17:
        return {
          main: 'hsla(210, 10%, 30%, 1)',
          light: 'hsla(210, 10%, 40%, 1)',
          dark: 'hsla(210, 10%, 20%, 1)',
        }
      default:
        return {
          main: 'hsla(150, 1%, 34%, 1)',
          light: 'hsla(150, 1%, 44%, 1)',
          dark: 'hsla(150, 1%, 24%, 1)',
        }
    }
  })
  return (
    <Grid item xs={12} lg={4} {...props}>
      <Stack
        direction="row"
        display="flex"
        color="white"
        height="100%"
        width="100%"
        boxShadow={2}
        borderRadius="5px"
        sx={
          pulse
            ? {
                overflow: 'visible',
                '@keyframes statusCardPulse': {
                  '0%': {
                    boxShadow:
                      '0 1px 3px rgba(0,0,0,0.2), 0 0 0 0 rgba(35, 154, 164, 0.55)',
                  },
                  '70%': {
                    boxShadow:
                      '0 1px 3px rgba(0,0,0,0.2), 0 0 0 12px rgba(35, 154, 164, 0)',
                  },
                  '100%': {
                    boxShadow:
                      '0 1px 3px rgba(0,0,0,0.2), 0 0 0 0 rgba(35, 154, 164, 0)',
                  },
                },
                animation: 'statusCardPulse 2s ease-out infinite',
              }
            : { overflow: 'hidden' }
        }
      >
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          padding={2}
          // bgcolor={colors.main}
          bgcolor={contrast ? 'contrast.main' : 'secondary.main'}
          justifyContent="space-between"
          borderRadius="5px 0 0 5px"
          position="relative"
          // Clips the oversized corner icon - the outer Stack can't when the
          // pulse ring needs overflow visible
          overflow="hidden"
          sx={{ cursor: 'pointer' }}
          onClick={() => (url ? navigate(url) : onClick && onClick())}
        >
          <Box position="absolute" bottom={-24} left={-24}>
            <SvgIcon
              component={icon}
              sx={{
                fontSize: '7rem',
                color: 'white',
                opacity: 0.2,
                zIndex: -1,
              }}
            />
          </Box>
          <Stack gap={1} alignItems="flex-end">
            <Typography
              variant="h6"
              textAlign="right"
              sx={{ zIndex: 2, opacity: 0.9 }}
              color="primary.contrastText"
            >
              {label}
            </Typography>
            <Stack direction="row" gap={1} alignItems="center">
              {!noNumber && (
                <Chip
                  variant="contained"
                  label={
                    loading ? (
                      <CircularProgress
                        size="1rem"
                        style={{ color: 'white' }}
                      />
                    ) : (
                      number
                    )
                  }
                  // label={number}
                  sx={{
                    backgroundColor: contrast
                      ? 'secondary.main'
                      : 'contrast.main',
                    zIndex: 2,
                    width: '4rem',
                    borderWidth: 0.5,
                    border: 'solid',
                    borderColor: contrast ? 'secondary.light' : 'contrast.light',
                    borderRadius: 1,
                    color: 'whitesmoke',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
                  }}
                  component={Paper}
                  elevation={1}
                />
              )}
              {button && (
                <Chip
                  variant="contained"
                  label={button}
                  sx={{
                    backgroundColor: colors.dark,
                    zIndex: 2,
                    borderWidth: 0.5,
                    border: 'solid',
                    borderColor: colors.light,
                    borderRadius: 1,
                    color: 'whitesmoke',
                  }}
                  component={Paper}
                  elevation={1}
                />
              )}
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Grid>
  )
}

export default StatusCard
